import { z } from "zod"

import type { DocumentFileType, StoreConfig } from "./types"

/** BMS caps a menu file's title at 60 characters; a longer one is cut, not dropped. */
export const MENU_FILE_TITLE_MAX_LENGTH = 60

/** A menu file with its fields validated. */
export interface ResolvedMenuFile {
  url: string
  type: DocumentFileType
  /** Never blank: the link text, and an image's alt text. */
  title: string
}

const documentFileSchema = z
  .object({
    url: z.url({ protocol: /^https?$/ }),
    title: z.string().trim().min(1),
    type: z.unknown().optional(),
  })
  .transform(
    ({ url, title, type }): ResolvedMenuFile => ({
      url,
      type: type === "image" ? "image" : "pdf",
      title: title.slice(0, MENU_FILE_TITLE_MAX_LENGTH),
    })
  )

/**
 * Narrow a tenant's raw `menuFiles`. Returns `null` — no link anywhere —
 * unless `isEnabled` is exactly `true` and at least one file survives:
 *   - a file without a valid `http(s)` URL or a non-blank title is dropped (a
 *     half-filled row never renders a dead or unlabelled link); the rest keep
 *     their order;
 *   - a `type` other than `image` (absent, unknown) is a PDF;
 *   - a title past 60 characters is cut.
 */
export function resolveMenuFiles(
  config: Pick<StoreConfig, "menuFiles">
): ResolvedMenuFile[] | null {
  const menuFiles = config.menuFiles
  if (menuFiles?.isEnabled !== true || !Array.isArray(menuFiles.files))
    return null
  const files = menuFiles.files.flatMap((file) => {
    const parsed = documentFileSchema.safeParse(file)
    return parsed.success ? [parsed.data] : []
  })
  return files.length > 0 ? files : null
}
